'use client'

import Container from '#/components/layout/Container'
import Section from '#/components/layout/Section'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { Label } from '#/components/ui/label'
import { Textarea } from '#/components/ui/textarea'
import { getHours, getSiteConfig } from '#/lib/content'
import { useCmsContent, useCmsObjectContent } from '#/hooks/useCmsContent'
import EditablePageHeader from '#/components/cms/EditablePageHeader'
import { submitContactForm } from '#/lib/server-actions'
import { contactFormSchema } from '#/lib/form-schemas'
import { createFileRoute } from '@tanstack/react-router'
import { Clock, Loader2, Mail, MapPin, Phone, Send } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { seoMeta, canonicalLink } from '#/lib/seo'

export const Route = createFileRoute('/contact')({
  head: () => ({
    meta: seoMeta({
      title: 'Contact',
      description:
        'Get in touch with Main Street Bread in Ferndale, MI. Place a special order, ask a question, or find our hours and location.',
      path: '/contact',
    }),
    links: [canonicalLink('/contact')],
  }),
  loader: async () => {
    const [hours, config] = await Promise.all([getHours(), getSiteConfig()])
    return { hours, config }
  },
  component: ContactPage,
})

function ContactPage() {
  const data = Route.useLoaderData()
  const hours = useCmsContent('hours', data.hours)
  const config = useCmsObjectContent('site-config', data.config)
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  })
  const [honeypot, setHoneypot] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const today = new Date().getDay()

  function updateField<K extends keyof typeof formState>(key: K, value: string) {
    setFormState((s) => ({ ...s, [key]: value }))
    if (fieldErrors[key]) {
      setFieldErrors((e) => ({ ...e, [key]: undefined }))
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const parsed = contactFormSchema.safeParse({ ...formState, website: honeypot })
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors
      const next: Record<string, string | undefined> = {}
      for (const [k, v] of Object.entries(flat)) next[k] = v?.[0]
      setFieldErrors(next)
      return
    }
    setFieldErrors({})
    setSubmitting(true)
    try {
      await submitContactForm({ data: parsed.data })
      setSubmitted(true)
    } catch (err) {
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : 'Something went wrong. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main>
      <Section tone="surface" className="py-12 sm:py-16">
        <Container>
          <EditablePageHeader page="contact" />
        </Container>
      </Section>

      <Section tone="low">
        <Container>
          <div className="grid gap-8 md:grid-cols-5">
            {/* Contact Form — takes more space */}
            <div className="md:col-span-3">
              {submitted ? (
                <div className="rounded-lg bg-surface-lowest p-10 text-center animate-[fadeSlideUp_500ms_ease-out]" style={{ boxShadow: 'var(--shadow-ambient)' }}>
                  <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-primary-container/15">
                    <Send className="size-6 text-primary-container" />
                  </div>
                  <p className="font-serif text-2xl font-semibold text-on-surface">Message Sent!</p>
                  <p className="mt-2 text-on-surface-variant">
                    Thank you for reaching out. We&apos;ll get back to you as soon as possible.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5 rounded-lg bg-surface-lowest p-6 py-8 sm:p-8 sm:py-10" style={{ boxShadow: 'var(--shadow-ambient)' }}>
                  <h3 className="font-serif text-xl font-bold text-on-surface">Send Us a Message</h3>
                  {/* Honeypot — hidden from users; bots will fill it. */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    aria-hidden="true"
                    className="sr-only"
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="ct-name">Name</Label>
                      <Input
                        id="ct-name"
                        value={formState.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        aria-invalid={!!fieldErrors.name}
                        className="bg-surface-highest"
                      />
                      {fieldErrors.name && (
                        <p className="mt-1 text-xs text-destructive">{fieldErrors.name}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="ct-email">Email</Label>
                      <Input
                        id="ct-email"
                        type="email"
                        value={formState.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        aria-invalid={!!fieldErrors.email}
                        className="bg-surface-highest"
                      />
                      {fieldErrors.email && (
                        <p className="mt-1 text-xs text-destructive">{fieldErrors.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="ct-phone">Phone (optional)</Label>
                      <Input
                        id="ct-phone"
                        type="tel"
                        value={formState.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        className="bg-surface-highest"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ct-subject">Subject</Label>
                      <select
                        id="ct-subject"
                        value={formState.subject}
                        onChange={(e) => updateField('subject', e.target.value)}
                        aria-invalid={!!fieldErrors.subject}
                        className="w-full rounded-md bg-surface-highest px-3 py-2 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <option value="">Select a subject...</option>
                        <option value="general">General Question</option>
                        <option value="order">Special Order</option>
                        <option value="catering">Catering Inquiry</option>
                        <option value="feedback">Feedback</option>
                        <option value="other">Other</option>
                      </select>
                      {fieldErrors.subject && (
                        <p className="mt-1 text-xs text-destructive">{fieldErrors.subject}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ct-message">Message</Label>
                    <Textarea
                      id="ct-message"
                      rows={6}
                      value={formState.message}
                      onChange={(e) => updateField('message', e.target.value)}
                      aria-invalid={!!fieldErrors.message}
                      placeholder="How can we help?"
                      className="bg-surface-highest"
                    />
                    {fieldErrors.message && (
                      <p className="mt-1 text-xs text-destructive">{fieldErrors.message}</p>
                    )}
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full rounded-full bg-primary hover:bg-primary-container text-white">
                    {submitting ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 size-4" />
                    )}
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>

            {/* Sidebar info */}
            <div className="space-y-6 md:col-span-2">
              {/* Map */}
              <div className="overflow-hidden rounded-lg" style={{ boxShadow: 'var(--shadow-ambient)' }}>
                <iframe
                  src={config.mapEmbedUrl}
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Main Street Bread location"
                />
              </div>

              {/* Contact details */}
              <div className="rounded-lg bg-surface-lowest p-5" style={{ boxShadow: 'var(--shadow-ambient)' }}>
                <ul className="space-y-3 text-sm text-on-surface-variant">
                  <li className="flex items-start gap-2">
                    <MapPin className="mt-0.5 size-4 shrink-0 text-primary-container" />
                    <span>
                      {config.address.street}, {config.address.city}, {config.address.state} {config.address.zip}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Phone className="size-4 shrink-0 text-primary-container" />
                    <span>{config.phone}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="size-4 shrink-0 text-primary-container" />
                    <span>{config.email}</span>
                  </li>
                </ul>
              </div>

              {/* Hours */}
              <div className="rounded-lg bg-surface-lowest p-5" style={{ boxShadow: 'var(--shadow-ambient)' }}>
                <h3 className="mb-3 flex items-center gap-2 font-serif text-base font-bold text-on-surface">
                  <Clock className="size-4 text-primary-container" />
                  Hours
                </h3>
                <div className="space-y-1.5">
                  {hours.map((h) => (
                    <div
                      key={h.dayOfWeek}
                      className={`flex justify-between text-sm ${h.dayOfWeek === today
                          ? 'font-semibold text-primary'
                          : 'text-on-surface-variant'
                        }`}
                    >
                      <span>{h.dayName}</span>
                      <span>{h.closed ? 'Closed' : `${h.open} – ${h.close}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </main>
  )
}
